import React from 'react'
import { OrderItem } from '../../types/Order'
import { Product, Variant } from '../../types/Product'
import { DeliveryProvider } from '../../types/DeliveryProviders'
import { mapToOrderItem, NewOrderItem } from '../../helpers/mapToOrderItem'
import { mergeProducts, DELIVERY_PROVIDER_TYPES, scrollToRef } from './createOrderHelpers'
import { getTaxFactor } from '../../helpers/getTaxFactor'

interface UseProductOperationsParams {
  storeId: string | undefined
  getCateringProduct: (productId: string) => Promise<Product | undefined>
  setProductsToAdd: React.Dispatch<React.SetStateAction<OrderItem[]>>
  setProductToUpdate: React.Dispatch<React.SetStateAction<OrderItem | null>>
  setShowAddProduct: React.Dispatch<React.SetStateAction<boolean>>
  setUpdateProduct: React.Dispatch<React.SetStateAction<boolean>>
  setAddProductError: React.Dispatch<React.SetStateAction<boolean>>
  setCreateOrderError: React.Dispatch<React.SetStateAction<string | null>>
  customerRef: React.RefObject<HTMLDivElement | null>
}

export function useProductOperations(params: UseProductOperationsParams) {
  const {
    storeId,
    getCateringProduct,
    setProductsToAdd,
    setProductToUpdate,
    setShowAddProduct,
    setUpdateProduct,
    setAddProductError,
    setCreateOrderError,
    customerRef,
  } = params

  const handleAddProduct = async (productToAdd: NewOrderItem | null) => {
    setAddProductError(false)
    setCreateOrderError(null)
    setShowAddProduct(false)
    
    if (!productToAdd) return

    try {
      const productFromApi = await getCateringProduct(productToAdd.productId)
      const newOrderItem = mapToOrderItem(productToAdd, productFromApi as Product, storeId)
      setProductsToAdd(prev => mergeProducts(prev, newOrderItem))
    } catch (error) {
      console.error(error)
    }
  }

  const handleUpdateProduct = (productToUpdate: OrderItem | null) => {
    if (!productToUpdate) {
      setUpdateProduct(false)
      return
    }

    const qty = productToUpdate.amount ?? productToUpdate.quantity ?? 0
    
    if (qty === 0) {
      setProductsToAdd(prev => prev.filter(p => p.id !== productToUpdate.id))
    } else {
      setProductsToAdd(prev =>
        prev.map(p => (p.id === productToUpdate.id ? productToUpdate : p))
      )
    }

    setProductToUpdate(null)
    setUpdateProduct(false)
    
    scrollToRef(customerRef)
  }

  const handleAmountChange = (
    newCount: number | string,
    id: string
  ) => {
    const count = typeof newCount === 'number' ? newCount : parseInt(newCount as string) || 0
    
    setProductToUpdate(prev => {
      if (!prev || prev.id !== id) return prev
      
      const newSubTotal = (prev.priceGross || prev.price) * count
      
      if (
        prev.amount === count &&
        prev.quantity === count &&
        prev.subTotal === newSubTotal
      ) {
        return prev
      }
      
      return { ...prev, amount: count, quantity: count, subTotal: newSubTotal }
    })
  }

  const handleSelectVariant = (
    orderItemId: string,
    productPrice: number,
    taxCode: number,
    variant: Variant
  ) => {
    const variantPrice = variant.price / 100
    const hasVariant = variantPrice !== 0
    const newGrossPrice = hasVariant ? variantPrice : productPrice / 100
    const vatPerUnit = newGrossPrice - newGrossPrice * getTaxFactor(taxCode)

    setProductToUpdate(prev => {
      if (!prev || prev.id !== orderItemId) return prev

      const currentQty = prev.amount ?? prev.quantity ?? 1
      const newSubTotal = newGrossPrice * currentQty

      if (
        prev.variantId === variant.id &&
        prev.variantName === variant.name &&
        prev.priceGross === newGrossPrice &&
        prev.vatSubTotal === vatPerUnit
      ) {
        return prev
      }

      return {
        ...prev,
        variantId: variant.id,
        variantName: variant.name,
        priceGross: newGrossPrice,
        vatSubTotal: vatPerUnit,
        subTotal: newSubTotal,
      }
    })
  }

  const handleCustomerCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value
    setProductToUpdate(prev =>
      prev ? (prev.message === val ? prev : { ...prev, message: val }) : prev
    )
  }

  return {
    handleAddProduct,
    handleUpdateProduct,
    handleAmountChange,
    handleSelectVariant,
    handleCustomerCommentChange,
  }
}

export function useDeliveryProviders(
  deliveryProviders: DeliveryProvider[]
) {
  const [hasMultipleDeliveryProviders, setHasMultipleDeliveryProviders] = React.useState(false)

  React.useEffect(() => {
    // Show delivery provider selection if:
    // - Multiple providers exist, OR
    // - Single provider is home delivery (needs address fields)
    const shouldShowSelection =
      deliveryProviders.length > 1 ||
      (deliveryProviders.length === 1 &&
        deliveryProviders[0].deliveryProviderType === DELIVERY_PROVIDER_TYPES.HOME_DELIVERY)
    
    setHasMultipleDeliveryProviders(shouldShowSelection)
  }, [deliveryProviders])

  return {
    hasMultipleDeliveryProviders,
  }
}

export function shouldAutoSelectDeliveryProvider(provider: DeliveryProvider): boolean {
  return provider.deliveryProviderType === DELIVERY_PROVIDER_TYPES.PICKUP
}